import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import { getValueFromStateAction } from '../../utils/state.util';

export type UseControlStateProps<Type, Dependency extends Type | undefined = Type> = {
  controller?: (state: Type) => void;
  dependencies?: Dependency | (Dependency | undefined)[];
  initial: Type;
  strict?: boolean;
};

export type UseControlStateReturn<Type, Dependency> = [
  Type,
  Dispatch<SetStateAction<Type>>,
  Dependency | undefined,
];

/**
 * Hook for providing state that can be both controlled or uncontrolled by other states
 *
 * @param {UseControlStateProps} props
 * @returns {UseControlStateReturn}
 */
export default function useControlState<Type, Dependency extends Type | undefined = Type>({
  controller,
  dependencies,
  initial,
  strict = true,
}: UseControlStateProps<Type, Dependency>): UseControlStateReturn<Type, Dependency> {
  // #region Computed Values
  const controllingState = useMemo(
    () =>
      Array.isArray(dependencies)
        ? dependencies.find((state) => state !== undefined)
        : dependencies,
    [dependencies],
  );
  // #endregion

  // #region States abd Refs
  const [controlledState, setState] = useState(controllingState ?? initial);
  const controllingStateRef = useRef<Type>(undefined);
  const controlledStateRef = useRef(controlledState);
  controlledStateRef.current = controlledState;
  // #endregion

  // #region Handlers
  const setControlledState: Dispatch<SetStateAction<Type>> = useCallback(
    (state: SetStateAction<Type>) => {
      if (controllingState === undefined || !strict) {
        const newState = getValueFromStateAction(state, controlledStateRef.current);
        setState(newState);
        controller?.(newState);
      }

      if (controllingState !== undefined && controller) {
        controller(getValueFromStateAction(state, controllingState));
      }
    },
    [controllingState, strict, controller],
  );
  const setControlledStateRef = useRef(setControlledState);
  setControlledStateRef.current = setControlledState;
  // #endregion

  // #region Additional Computed Values
  const finalControlledState = useMemo(() => {
    if (!Object.is(controllingStateRef.current, controllingState)) {
      controllingStateRef.current = controllingState;

      return controllingState as Type;
    }

    return controlledState;
  }, [controlledState, controllingState]);
  // #endregion

  return [finalControlledState, setControlledStateRef.current, controllingState];
}
